import { Component, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { friendlyError } from '@/lib/errors'

interface Props { children: ReactNode; fallback?: ReactNode }
interface State { hasError: boolean; error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null }
  static getDerivedStateFromError(error: Error) { return { hasError: true, error } }

  componentDidCatch(error: Error) {
    // Auto-reload on chunk load failures (e.g. after a new deployment).
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      /Failed to fetch dynamically imported module|Importing a module script failed|Unable to preload CSS/.test(
        error?.message || '',
      )
    if (isChunkError && !sessionStorage.getItem('chunk-reloaded')) {
      sessionStorage.setItem('chunk-reloaded', String(Date.now()))
      window.location.reload()
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback
      // Friendly, human FR message — NEVER a raw stack trace / undefined-property throw.
      // This is what keeps an invalid-context page from white-screening: the throw
      // is caught here and the content area shows a recoverable error instead.
      return (
        <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-destructive/10">
            <AlertTriangle className="h-6 w-6 text-destructive" />
          </div>
          <h2 className="text-lg font-semibold text-foreground">Une erreur est survenue</h2>
          <p className="max-w-sm text-sm text-muted-foreground">
            {friendlyError(this.state.error, "Cette section n'a pas pu s'afficher. Réessayez.")}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-1 inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Réessayer
          </button>
        </div>
      )
    }
    return this.props.children
  }
}
